import React, { useContext, useEffect, useRef, useState } from 'react';
import {Button, Card, message, Row, Upload, UploadFile} from "antd";
import {useAppDispatch, useAppSelector} from "./store/hooks.ts";
import {startPeer, stopPeerSession} from "./store/peer/peerActions.ts";
import * as connectionAction from "./store/connection/connectionActions.ts"
import {PeerConnection, TransferProgress} from "./helpers/peer.ts";
import {useAsyncState} from "./helpers/hooks.ts";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import { faClose} from "@fortawesome/free-solid-svg-icons"
import { useStore } from '../store.jsx';
import { langContext } from '../App.js';
import { uploadPeer } from './fileUploadPeerTranslate.js';
import { socket } from '../const/socket';
import SOCKET from '../const/socketNames';

// how long the sender waits for the other person to accept
const REQUEST_TIMEOUT_MS = 60000

type Status = "starting" | "waiting" | "accepted" | "declined" | "timeout" | "unavailable" | "error"

// mode "request": this user asked to send files and must wait for the other side to accept.
// mode "accept":  this user accepted an incoming request; only the requester may connect.
export const FileUploadPeer: React.FC = ({setIsOpenP2P, setFromP2P, mode = "request"}: any) => {

    const lang = useContext(langContext);
    let showBySelected = uploadPeer[lang] || uploadPeer.arm

    const peer = useAppSelector((state) => state.peer)
    const connection = useAppSelector((state) => state.connection)
    const dispatch = useAppDispatch()
    const { chatStore, usersStore } = useStore()

    // the person on the other side is fixed for the lifetime of this panel
    const target = useRef<string>(chatStore.username || "").current
    const [status, setStatus] = useState<Status>("starting")
    const statusRef = useRef<Status>("starting")
    // file currently being sent or received
    const [progress, setProgress] = useState<TransferProgress | null>(null)
    const updateStatus = (value: Status) => { statusRef.current = value; setStatus(value) }

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined
        let closed = false

        const onResponse = ({from, status: answer}: {from: string, status: Status}) => {
            if (from !== target || mode !== "request" || statusRef.current !== "waiting") return
            clearTimeout(timer)
            updateStatus(answer)
            if (answer === "accepted") {
                dispatch(connectionAction.connectPeer(target))
            }
        }
        socket.on(SOCKET.P2P_RESPONSE, onResponse)
        PeerConnection.setProgressListener((p) => setProgress(p.percent >= 100 ? null : p))

        const start = async () => {
            PeerConnection.allowPeer(target)
            dispatch(connectionAction.changeConnectionInput(target))
            await dispatch(startPeer())
            if (closed) return
            if (!PeerConnection.getPeer()?.open) {
                updateStatus("error")
                return
            }
            if (mode === "accept") {
                // our session is ready, now the requester may connect
                updateStatus("accepted")
                socket.emit(SOCKET.P2P_ACCEPT, {to: target})
            } else {
                updateStatus("waiting")
                socket.emit(SOCKET.P2P_REQUEST, {to: target})
                timer = setTimeout(() => {
                    if (statusRef.current !== "waiting") return
                    updateStatus("timeout")
                    socket.emit(SOCKET.P2P_CANCEL, {to: target})
                }, REQUEST_TIMEOUT_MS)
            }
        }
        start()

        // runs however the panel is closed (close button, outside click, chat switch)
        return () => {
            closed = true
            clearTimeout(timer)
            socket.off(SOCKET.P2P_RESPONSE, onResponse)
            PeerConnection.setProgressListener(undefined)
            if (mode === "request" && statusRef.current === "waiting") {
                socket.emit(SOCKET.P2P_CANCEL, {to: target})
            }
            PeerConnection.closePeerSession(target)
            dispatch(connectionAction.removeConnectionList(target))
            dispatch(stopPeerSession())
        }
    }, [])

    const [fileList, setFileList] = useAsyncState([] as UploadFile[])
    const [sendLoading, setSendLoading] = useAsyncState(false)

    const connected = status === "accepted" && connection.list.includes(target)

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning("Please select file")
            return
        }
        if (!connected) {
            message.warning(showBySelected.tr_notConnected)
            return
        }
        try {
            await setSendLoading(true);
            const file = fileList[0] as unknown as File;
            await PeerConnection.sendFile(target, file, setProgress)
            setProgress(null)
            await setSendLoading(false)
            message.info(showBySelected.tr_sucsessfullySent)
        } catch (err) {
            setProgress(null)
            await setSendLoading(false)
            message.error(showBySelected.tr_error)
        }
    }

    const name = usersStore.nameOf(target)
    const statusText = () => {
        switch (status) {
            case "waiting": return showBySelected.tr_waitingForAccept(name)
            case "declined": return showBySelected.tr_declined(name)
            case "timeout": return showBySelected.tr_noAnswer(name)
            case "unavailable": return showBySelected.tr_isntOnline
            case "error": return showBySelected.tr_error
            case "accepted": return connected ? showBySelected.tr_connectedWith(name) : showBySelected.tr_pleaseWait
            default: return showBySelected.tr_pleaseWait
        }
    }

    const closePanel = () => {
        setIsOpenP2P(false)
        setFromP2P(false)
    }

    return (
        <Row justify={"center"} align={"top"}>
            <Card>
                <div hidden={!peer.started && status !== "error"}>
                    <Card>
                        <div style={{textAlign: "center", maxWidth: 320}}>{statusText()}</div>
                        {progress && (
                            <div style={{marginTop: 12, maxWidth: 320}}>
                                <div style={{fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                    {progress.direction === "send" ? "↑" : "↓"} {progress.name} — {progress.percent}%
                                </div>
                                <div style={{height: 6, background: "#E5E7EB", borderRadius: 3, overflow: "hidden", marginTop: 4}}>
                                    <div style={{width: `${progress.percent}%`, height: 6, background: "#195FFC", transition: "width .2s"}} />
                                </div>
                            </div>
                        )}
                    </Card>
                    <Card>
                        <Upload fileList={fileList}
                                maxCount={1}
                                onRemove={() => setFileList([])}
                                beforeUpload={(file) => {
                                    setFileList([file])
                                    return false
                                }}>
                            <Button disabled={!connected}>{showBySelected.tr_chooseFile}</Button>
                        </Upload>
                        <Button
                            className='bg-blue-600'
                            type="primary"
                            onClick={handleUpload}
                            disabled={fileList.length === 0 || !connected}
                            loading={sendLoading}
                            style={{marginTop: 16}}
                        >
                            {sendLoading ? showBySelected.tr_sending : showBySelected.tr_send}
                        </Button>
                    </Card>
                </div>
            </Card>

            <div className="flex items-center justify-between mb-2 space-x-2" onClick={closePanel}>
                <button type="button"
                        className="inline-flex items-center justify-center rounded-lg border h-10 w-10 transition duration-500 ease-in-out text-gray-500 hover:bg-gray-300 focus:outline-none">
                    <FontAwesomeIcon icon={faClose}/>
                </button>
            </div>
        </Row>
    )
}

export default FileUploadPeer
